package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.CreateStudentRequest
import com.elan.edubridge.shared.domain.model.GuardianContact
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.UpdateStudentRequest
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement

/**
 * 학생 관리(가이드 §3.6). 목록은 [StudentData]가 실제로 들고 있는 필드(이름·학년·연락처·보호자 연락처)만
 * 보여준다 — 가이드 표의 "소속 반"·"상태(재원)" 컬럼은 대응하는 데이터가 없어 뺐다(계획 §2.5 계약에
 * 그 필드가 없다 — 지어내지 않는다).
 *
 * **알려진 계약 공백**: 가이드/오케스트레이터 지시는 "보호자 링크 해지"(`revokeGuardianLink`) 버튼도
 * 요구하지만, 이 시점에 `AcademyCommandRepository`·`EduConnectRepository` 어디에도 해당 커맨드와
 * 그 전제가 되는 "학생별 GuardianLink 목록 조회"가 없다(§2.4의 `guardianLinks(guardianUserId)`는
 * 보호자 기준 조회라 반대 방향이다). `shared` 소스 전체가 소유 범위 밖이라 직접 추가하지 않았다 — 두 계약이
 * 착지하면 이 파일에 붙이면 된다. 여기서 다루는 "보호자"는 학생 문서에 실린 [GuardianContact](이름·연락처)뿐이다.
 */
object StudentsPage {
    private val scope = MainScope()

    /** 명단 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "students-retry"
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    private var allStudents: List<StudentData> = emptyList()

    fun open(session: SessionState, academyId: String) {
        if (session.activeMembership?.academyId != academyId || !session.activePermission.atLeast(Permission.EDITOR)) {
            Router.replace("/app")
            return
        }
        scope.launch {
            val result = repository.studentsResult(academyId)
            allStudents = result.items
            render(session, academyId, allStudents, result.failure)
        }
    }

    private fun render(
        session: SessionState,
        academyId: String,
        students: List<StudentData>,
        failure: CommandFailure?,
    ) {
        DashboardShell.render(session, academyId, "students", contentHtml(session, students, failure)) {
            wireRetry(RETRY_ID) { open(session, academyId) }
            wireList(session, academyId)
        }
    }

    private fun contentHtml(
        session: SessionState,
        students: List<StudentData>,
        failure: CommandFailure?,
    ): String = """
        <div class="eb-page-header">
            <h1 class="eb-page-title">학생</h1>
            <div>
                <button class="eb-button eb-button--ghost" id="manage-classes" type="button">반 관리</button>
                <button class="eb-button eb-button--primary" id="add-student">${icon("icon-plus", "sm")}학생 추가</button>
            </div>
        </div>
        <div class="eb-toolbar">
            <div class="eb-search">${icon("icon-search", "sm")}<input type="search" id="student-search" placeholder="이름으로 찾기" aria-label="학생 이름 검색"></div>
        </div>
        <section class="eb-card">
            <div class="eb-table-scroll">
                <table class="eb-table eb-table--students">
                    <thead>
                        <tr><th scope="col">이름</th><th scope="col">학년</th><th scope="col">연락처</th><th scope="col">보호자 연락처</th><th scope="col"><span class="eb-sr-only">관리</span></th></tr>
                    </thead>
                    <tbody id="student-rows">${rowsHtml(session, students, failure)}</tbody>
                </table>
            </div>
        </section>
    """.trimIndent()

    /**
     * [failure]는 **명단 조회 실패**일 때만 non-null이다. 검색 필터 결과가 비어 있는 경우는
     * 항상 null로 부른다 — 필터로 0건인 것은 읽기 실패가 아니다(#30).
     */
    private fun rowsHtml(
        session: SessionState,
        students: List<StudentData>,
        failure: CommandFailure?,
    ): String {
        if (students.isEmpty()) {
            // 명단이 비어 보이는 것과 못 읽는 것은 학원장에게 완전히 다른 정보다.
            val body = if (failure != null) {
                readErrorHtml(failure, RETRY_ID)
            } else {
                """<div class="eb-empty-state">${icon("icon-students", "xl")}<h2>등록된 학생이 없어요</h2><p>학생을 직접 추가하거나 초대 링크를 보내 가입을 받을 수 있어요.</p></div>"""
            }
            return """<tr><td colspan="5">$body</td></tr>"""
        }
        val canIssueInvitation = !session.requiresGuardianConsent && session.activePermission.atLeast(Permission.EDITOR)
        return students.joinToString("") { student ->
            val guardianSummary = student.guardianContacts.firstOrNull()?.let { first ->
                val extra = if (student.guardianContacts.size > 1) """ <span class="eb-badge-count" title="보호자 ${student.guardianContacts.size}명">+${student.guardianContacts.size - 1}</span>""" else ""
                "${first.relation.escapeHtml()} ${maskPhone(first.phone)}$extra"
            } ?: "-"
            """
            <tr data-student-id="${student.id}">
                <td>${student.name.escapeHtml()}</td>
                <td>${student.grade.escapeHtml()}</td>
                <td>${student.phone?.let { maskPhone(it) } ?: "-"}</td>
                <td>$guardianSummary</td>
                <td>
                    ${if (canIssueInvitation) """<button class="eb-button eb-button--ghost eb-button--sm issue-student-invitation" data-student-id="${student.id}" type="button">초대</button>""" else ""}
                    <button class="eb-icon-button eb-icon-button--sm edit-student" data-student-id="${student.id}" aria-label="${student.name.escapeHtml()} 학생 편집">${icon("icon-edit", "sm")}</button>
                </td>
            </tr>
            """.trimIndent()
        }
    }

    private fun wireList(session: SessionState, academyId: String) {
        (document.getElementById("student-search") as? HTMLInputElement)?.oninput = { event ->
            val query = (event.target as HTMLInputElement).value.trim()
            val filtered = if (query.isEmpty()) allStudents else allStudents.filter { it.name.contains(query, ignoreCase = true) }
            (document.getElementById("student-rows") as? HTMLElement)?.let {
                // 검색 결과가 0건인 것은 읽기 실패가 아니므로 failure는 항상 null이다.
                it.innerHTML = rowsHtml(session, filtered, failure = null)
                wireRowButtons(session, academyId)
            }
            null
        }
        (document.getElementById("add-student") as? HTMLButtonElement)?.onclick = {
            openDrawer(session, academyId, existing = null)
            null
        }
        (document.getElementById("manage-classes") as? HTMLButtonElement)?.onclick = {
            ClassEditModal.open(session, academyId)
            null
        }
        wireRowButtons(session, academyId)
    }

    private fun wireRowButtons(session: SessionState, academyId: String) {
        val invitationButtons = document.querySelectorAll(".issue-student-invitation")
        for (index in 0 until invitationButtons.length) {
            val button = invitationButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val studentId = button.getAttribute("data-student-id")
                allStudents.firstOrNull { it.id == studentId }?.let { InvitationIssueModal.openForStudent(session, academyId, it) }
                null
            }
        }
        val buttons = document.querySelectorAll(".edit-student")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                val studentId = button.getAttribute("data-student-id")
                allStudents.firstOrNull { it.id == studentId }?.let { openDrawer(session, academyId, existing = it) }
                null
            }
        }
    }

    private fun openDrawer(session: SessionState, academyId: String, existing: StudentData?) {
        val guardians = existing?.guardianContacts.orEmpty().toMutableList()

        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-drawer"
        backdrop.innerHTML = """
            <div class="eb-drawer__backdrop"></div>
            <div class="eb-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="student-drawer-title">
                <header class="eb-drawer__header">
                    <h2 id="student-drawer-title">${if (existing == null) "학생 추가" else "${existing.name.escapeHtml()} 학생"}</h2>
                    <button class="eb-icon-button" id="drawer-close" aria-label="닫기">${icon("icon-close", "md")}</button>
                </header>
                <form class="eb-drawer__body" id="student-form">
                    <fieldset class="eb-fieldset">
                        <legend>기본 정보</legend>
                        <div class="eb-field"><label for="s-name">이름</label><input id="s-name" value="${existing?.name?.escapeHtml() ?: ""}" required></div>
                        <div class="eb-field"><label for="s-grade">학년</label><input id="s-grade" value="${existing?.grade?.escapeHtml() ?: ""}"></div>
                        <div class="eb-field"><label for="s-phone">학생 연락처</label><input id="s-phone" type="tel" value="${existing?.phone?.escapeHtml() ?: ""}" placeholder="010-0000-0000"></div>
                    </fieldset>
                    <fieldset class="eb-fieldset">
                        <legend>보호자 연락처</legend>
                        <div id="guardian-rows">${guardianRowsHtml(guardians)}</div>
                        <button type="button" class="eb-button eb-button--ghost eb-button--sm" id="add-guardian">${icon("icon-plus", "xs")}보호자 추가</button>
                        <p class="eb-meta">연락처 변경은 감사 로그에 기록돼요.</p>
                    </fieldset>
                    <p class="eb-form-error" role="alert" id="drawer-error" hidden></p>
                </form>
                <div class="eb-drawer__footer">
                    <button type="button" class="eb-button eb-button--ghost" id="drawer-cancel">취소</button>
                    <button type="submit" form="student-form" class="eb-button eb-button--primary" id="drawer-save">저장</button>
                </div>
            </div>
        """.trimIndent()
        document.body!!.appendChild(backdrop)

        fun close() = backdrop.remove()
        (backdrop.querySelector("#drawer-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#drawer-cancel") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector(".eb-drawer__backdrop") as? HTMLElement)?.onclick = { close(); null }

        fun rewireGuardianRows() {
            val container = backdrop.querySelector("#guardian-rows") as HTMLElement
            container.innerHTML = guardianRowsHtml(guardians)
            val deleteButtons = container.querySelectorAll(".guardian-delete")
            for (index in 0 until deleteButtons.length) {
                val deleteButton = deleteButtons.item(index) as? HTMLElement ?: continue
                deleteButton.onclick = {
                    val rowIndex = deleteButton.getAttribute("data-index")?.toIntOrNull()
                    if (rowIndex != null) guardians.removeAt(rowIndex)
                    rewireGuardianRows()
                    null
                }
            }
        }
        rewireGuardianRows()

        (backdrop.querySelector("#add-guardian") as? HTMLElement)?.onclick = {
            guardians += GuardianContact(name = "", phone = "", relation = "모")
            rewireGuardianRows()
            null
        }

        val form = backdrop.querySelector("#student-form")!!
        form.addEventListener("submit", { event ->
            event.preventDefault()
            submitStudent(session, academyId, existing, backdrop, guardians, ::close)
        })
    }

    private fun submitStudent(
        session: SessionState,
        academyId: String,
        existing: StudentData?,
        backdrop: HTMLElement,
        guardianRows: MutableList<GuardianContact>,
        close: () -> Unit,
    ) {
        val name = (backdrop.querySelector("#s-name") as HTMLInputElement).value.trim()
        val grade = (backdrop.querySelector("#s-grade") as HTMLInputElement).value.trim()
        val phone = (backdrop.querySelector("#s-phone") as HTMLInputElement).value.trim().ifEmpty { null }
        val guardianContacts = readGuardianRows(backdrop, guardianRows.size)
        val errorEl = backdrop.querySelector("#drawer-error") as HTMLElement

        if (name.isEmpty()) {
            errorEl.textContent = "이름을 입력해 주세요."
            errorEl.removeAttribute("hidden")
            return
        }

        scope.launch {
            val result = if (existing == null) {
                commandRepository.createStudent(
                    CreateStudentRequest(academyId, name, grade, phone, guardianContacts),
                )
            } else {
                commandRepository.updateStudent(
                    UpdateStudentRequest(academyId, existing.id, name, grade, phone, guardianContacts),
                )
            }
            if (result.isSuccess) {
                close()
                Toast.success("학생 정보를 저장했어요.")
                val refreshed = repository.studentsResult(academyId)
                allStudents = refreshed.items
                render(session, academyId, allStudents, refreshed.failure)
            } else {
                errorEl.textContent = result.errorMessage ?: "저장에 실패했어요. 다시 시도해 주세요."
                errorEl.removeAttribute("hidden")
            }
        }
    }

    private fun readGuardianRows(backdrop: HTMLElement, count: Int): List<GuardianContact> {
        val result = mutableListOf<GuardianContact>()
        for (index in 0 until count) {
            val nameInput = backdrop.querySelector("#guardian-name-$index") as? HTMLInputElement ?: continue
            val phoneInput = backdrop.querySelector("#guardian-phone-$index") as? HTMLInputElement ?: continue
            val relationSelect = backdrop.querySelector("#guardian-relation-$index") as? HTMLSelectElement ?: continue
            val name = nameInput.value.trim()
            val phone = phoneInput.value.trim()
            if (name.isEmpty() && phone.isEmpty()) continue
            result += GuardianContact(name = name, phone = phone, relation = relationSelect.value)
        }
        return result
    }

    private fun guardianRowsHtml(guardians: List<GuardianContact>): String {
        if (guardians.isEmpty()) return """<p class="eb-meta">등록된 보호자 연락처가 없어요.</p>"""
        return guardians.mapIndexed { index, guardian ->
            val relations = listOf("모", "부", "조부모", "기타")
            val options = relations.joinToString("") { option ->
                val selected = if (option == guardian.relation) " selected" else ""
                """<option$selected>$option</option>"""
            }
            """
            <div class="eb-guardian-row">
                <input id="guardian-name-$index" aria-label="보호자 이름" value="${guardian.name.escapeHtml()}" placeholder="이름">
                <select id="guardian-relation-$index" aria-label="관계">$options</select>
                <input id="guardian-phone-$index" aria-label="보호자 연락처" type="tel" value="${guardian.phone.escapeHtml()}" placeholder="010-0000-0000">
                <button type="button" class="eb-icon-button guardian-delete" data-index="$index" aria-label="이 보호자 삭제">${icon("icon-trash", "sm")}</button>
            </div>
            """.trimIndent()
        }.joinToString("")
    }

    /** 목록은 뒷자리 마스킹으로 노출한다(가이드 §3.6.1) — 다수 열람 지점이라 전체 노출을 최소화한다. */
    private fun maskPhone(phone: String): String {
        val digits = phone.filter { it.isDigit() }
        if (digits.length < 8) return phone
        return "${digits.take(3)}-****-${digits.takeLast(4)}"
    }
}
